import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // 'super_admin' or 'school_admin'
  const [tenantId, setTenantId] = useState(null);
  const [tenantName, setTenantName] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState(null); // For Super Admin viewing other schools
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load auth state from localStorage on mount
    const savedRole = localStorage.getItem('role');
    const savedTenantId = localStorage.getItem('tenantId');
    const savedTenantName = localStorage.getItem('tenantName');
    const savedSelectedTenantId = localStorage.getItem('selectedTenantId');
    
    if (savedRole) {
      setRole(savedRole);
      setTenantId(savedTenantId);
      setTenantName(savedTenantName);
      setSelectedTenantId(savedSelectedTenantId);
    }
    
    setLoading(false);
  }, []);

  const loginWithCredentials = (token, userData) => {
    const authData = {
      role: userData.role,
      tenantId: userData.tenant_id,
      tenantName: userData.tenant_name,
      selectedTenantId: null,
      username: userData.username
    };
    
    setRole(userData.role);
    setTenantId(userData.tenant_id);
    setTenantName(userData.tenant_name);
    setSelectedTenantId(null);
    setUser(userData);
    
    localStorage.setItem('token', token);
    localStorage.setItem('role', userData.role);
    localStorage.setItem('tenantId', userData.tenant_id || '');
    localStorage.setItem('tenantName', userData.tenant_name || '');
    localStorage.setItem('username', userData.username);
    localStorage.removeItem('selectedTenantId');
    
    return authData;
  };

  const selectTenant = (tenantId) => {
    // Only Super Admin can select different tenants
    if (role !== 'super_admin') return;
    
    setSelectedTenantId(tenantId);
    localStorage.setItem('selectedTenantId', tenantId);
  };

  const clearTenantSelection = () => {
    setSelectedTenantId(null);
    localStorage.removeItem('selectedTenantId');
  };

  const logout = () => {
    setUser(null);
    setRole(null);
    setTenantId(null);
    setTenantName(null);
    setSelectedTenantId(null);
    
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenantName');
    localStorage.removeItem('username');
    localStorage.removeItem('selectedTenantId');
  };

  // Get the active tenant ID (selected for Super Admin, own for School Admin)
  const getActiveTenantId = () => {
    if (role === 'super_admin') {
      return selectedTenantId; // null means view all
    }
    return tenantId; // School Admin's own tenant
  };

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const value = {
    user,
    role,
    tenantId,
    tenantName,
    selectedTenantId,
    loading,
    loginWithCredentials,
    selectTenant,
    clearTenantSelection,
    logout,
    getActiveTenantId,
    getToken,
    isSuperAdmin: role === 'super_admin',
    isSchoolAdmin: role === 'school_admin',
    isAuthenticated: !!role
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
